import FindConversationResponseMessage from 'owa-service/lib/contract/FindConversationResponseMessage';
import findConversationWithStartIndex from 'owa-mail-store/lib/services/findConversationWithStartIndex';
import FindItemResponseMessage from 'owa-service/lib/contract/FindItemResponseMessage';
import folderId from 'owa-service/lib/factory/folderId';
import getConversationSortByProperty from '../../utils/tables/getConversationSortByProperty';
import getFolderData from 'owa-mail-actions/lib/getFolderData';
import getMailItemSortByProperty from '../../utils/tables/getMailItemSortByProperty';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import TableView from 'owa-mail-list-store/lib/store/schema/TableView';
import { action } from 'satcheljs/lib/legacy';
import { actionNames } from 'owa-mail-store-actions';
import { findItemWithStartIndex } from 'owa-mail-store';
import {
    getBaseFolderId,
    getBaseServerFolderId,
    MailFolderTableQuery,
    TableQueryType,
} from 'owa-mail-list-store';
import { getMailboxInfoFromTableQuery } from 'owa-mail-mailboxinfo';
import { INITIAL_LOAD_ROW_COUNT } from './TableLoadingConstants';
import { logUsage } from 'owa-analytics';
import { onReloadTableFailed, onReloadTableSucceeded } from '../table/onReloadTableCompleted';
import getHashtagsToInclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToInclude';
import getHashtagsToExclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToExclude';

/**
 * Reload the given table view
 * @param tableView to be reloaded
 */
export default action(actionNames.ReloadMailListTableAction)(function reloadTable(
    tableView: TableView
) {
    if (tableView.tableQuery.type === TableQueryType.Search) {
        // search table cannot be reloaded via the Find calls
        return;
    }

    // let CI be currentLoadedIndex
    // Case 1: if CI < INITIAL_LOAD_ROW_COUNT, request INITIAL_LOAD_ROW_COUNT rows, the reason is server may have more rows
    // and we would like to fill our viewport with more rows
    // Case 2: if CI >= INITIAL_LOAD_ROW_COUNT, request CI rows
    // In case 2 we would want to optimize reload performance,
    // since we currently will load all items that are in currentLoadedIndex - VSO: 11814
    const rowsToReload = Math.max(tableView.currentLoadedIndex, INITIAL_LOAD_ROW_COUNT);

    // Reload all items that we have, otherwise rows may be in an inconsistent state
    // We need to pass in the reload datapoint, because it is used in the async callback to log the service error
    // TODO: 11814: Optimize reload performance since we currently reload all items that we have
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            reloadConversationTable(tableView, rowsToReload);
            break;

        case ReactListViewType.Message:
            reloadItemTable(tableView, rowsToReload);
            break;
    }

    // Also refresh counts for folder
    if (tableView.tableQuery.type == TableQueryType.Folder) {
        getFolderData(tableView.tableQuery.folderId);
    }

    // log number of rows reloaded
    logUsage('ReloadTable', [rowsToReload]);
});

/**
 * Reload the conversation table
 * @param tableView the conversation table view to be reloaded
 * @param rowsToReload the number of rows to reload
 */
function reloadConversationTable(tableView: TableView, rowsToReload: number) {
    const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;
    findConversationWithStartIndex(
        getBaseFolderId(mailTableQuery),
        0 /* startIndex */,
        rowsToReload,
        mailTableQuery.viewFilter,
        getConversationSortByProperty(mailTableQuery),
        mailTableQuery.focusedViewFilter,
        mailTableQuery.requestShapeName,
        mailTableQuery.shouldTryToUseSessionDataResponse,
        getMailboxInfoFromTableQuery(mailTableQuery).requestOptions,
        mailTableQuery.categoryName,
        getHashtagsToInclude(mailTableQuery.inboxClassificationFilter),
        getHashtagsToExclude(mailTableQuery.inboxClassificationFilter),
        null /*findConversationDatapoint*/,
        getBaseServerFolderId(tableView, true /* returnNullIfSameAsFolderId */)
    )
        .then(
            (response: {
                findConversationResponseMessage: FindConversationResponseMessage;
                responseInit: ResponseInit;
            }) => {
                const responseMessage = response.findConversationResponseMessage;
                if (responseMessage.ResponseClass == 'Success') {
                    onReloadTableSucceeded(
                        tableView,
                        responseMessage.Conversations,
                        responseMessage.TotalConversationsInView
                    );
                } else {
                    onReloadTableFailed(responseMessage.ResponseCode);
                }
            }
        )
        .catch(error => {
            onReloadTableFailed();
        });
}

/**
 * Reload the item table
 * @param tableView the item table view to be reloaded
 * @param number of rows to reload
 */
function reloadItemTable(tableView: TableView, rowsToReload: number): void {
    const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;
    findItemWithStartIndex(
        getBaseFolderId(mailTableQuery),
        0 /* startIndex */,
        rowsToReload,
        mailTableQuery.viewFilter,
        getMailItemSortByProperty(mailTableQuery),
        mailTableQuery.focusedViewFilter,
        mailTableQuery.requestShapeName,
        mailTableQuery.shouldTryToUseSessionDataResponse,
        getMailboxInfoFromTableQuery(mailTableQuery).requestOptions,
        mailTableQuery.categoryName,
        getHashtagsToInclude(mailTableQuery.inboxClassificationFilter),
        getHashtagsToExclude(mailTableQuery.inboxClassificationFilter),
        tableView.serverFolderId && tableView.serverFolderId != mailTableQuery.folderId
            ? folderId({ Id: tableView.serverFolderId })
            : null
    )
        .then((responseMessage: FindItemResponseMessage) => {
            if (responseMessage.ResponseClass == 'Success') {
                onReloadTableSucceeded(
                    tableView,
                    responseMessage.RootFolder.Items,
                    responseMessage.RootFolder.TotalItemsInView
                );
            } else {
                onReloadTableFailed(responseMessage.ResponseCode);
            }
        })
        .catch(error => {
            onReloadTableFailed();
        });
}
